import React, { useState, useEffect } from 'react';
import './AddRequisiteModal.css';
import DetailsPortal from './DetailsPortal';

const AddPriceModal = ({ isOpen, onClose, onSubmit, initialData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    fiat: '',
    gwPayIn: '',
    gwPayOut: '',
    payIn: '',
    payOut: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fill the form with data while editing
  useEffect(() => {
    if (isOpen && initialData && isEdit) {
      setFormData({
        fiat: initialData.fiat || initialData.Fiat || initialData.FIAT || '',
        gwPayIn: initialData.gwPayIn || initialData.GwPayIn || initialData.GWPAYIN || '',
        gwPayOut: initialData.gwPayOut || initialData.GwPayOut || initialData.GWPAYOUT || '',
        payIn: initialData.payIn || initialData.PayIn || initialData.PAY_IN || '',
        payOut: initialData.payOut || initialData.PayOut || initialData.PAY_OUT || ''
      });
    } else if (isOpen && !isEdit) {
      // Reset the form when creating
      setFormData({
        fiat: '',
        gwPayIn: '',
        gwPayOut: '',
        payIn: '',
        payOut: ''
      });
    }
  }, [isOpen, initialData, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation
      if (!formData.fiat || !formData.gwPayIn || !formData.gwPayOut || !formData.payIn || !formData.payOut) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Check numeric values
      if (isNaN(parseFloat(formData.gwPayIn)) || parseFloat(formData.gwPayIn) <= 0) {
        setError('GwPayIn must be a positive number');
        setLoading(false);
        return;
      }

      if (isNaN(parseFloat(formData.gwPayOut)) || parseFloat(formData.gwPayOut) <= 0) {
        setError('GwPayOut must be a positive number');
        setLoading(false);
        return;
      }

      if (isNaN(parseFloat(formData.payIn)) || parseFloat(formData.payIn) <= 0) {
        setError('PayIn must be a positive number');
        setLoading(false);
        return;
      }

      if (isNaN(parseFloat(formData.payOut)) || parseFloat(formData.payOut) <= 0) {
        setError('PayOut must be a positive number');
        setLoading(false);
        return;
      }

      // When editing, also pass the original data
      const result = isEdit 
        ? await onSubmit(formData, initialData)
        : await onSubmit(formData);
      
      if (result.success) {
        // Clear the form and close the modal immediately after a successful response
        setFormData({
          fiat: '',
          gwPayIn: '',
          gwPayOut: '',
          payIn: '',
          payOut: ''
        });
        setLoading(false);
        onClose();
      } else {
        setError(result.error || (isEdit ? 'Failed to update price' : 'Failed to create price'));
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        fiat: '',
        gwPayIn: '',
        gwPayOut: '',
        payIn: '',
        payOut: ''
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <DetailsPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit price' : 'Add price'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="requisite-form">
          <div className="form-group">
            <label htmlFor="fiat">
              Fiat <span className="required">*</span>
            </label>
            <input
              type="text"
              id="fiat"
              name="fiat"
              value={formData.fiat}
              onChange={handleInputChange}
              placeholder="e.g. AZN, USD, EUR"
              required
              disabled={loading}
              maxLength={10}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="gwPayIn">
              GwPayIn rate <span className="required">*</span>
            </label>
            <input
              type="number"
              id="gwPayIn"
              name="gwPayIn"
              value={formData.gwPayIn}
              onChange={handleInputChange}
              placeholder="e.g. 47.50"
              required
              disabled={loading}
              step="0.01"
              min="0.01"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="gwPayOut">
              GwPayOut rate <span className="required">*</span>
            </label>
            <input
              type="number"
              id="gwPayOut"
              name="gwPayOut"
              value={formData.gwPayOut}
              onChange={handleInputChange}
              placeholder="e.g. 45.70"
              required
              disabled={loading}
              step="0.01"
              min="0.01"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="payIn">
              PayIn rate <span className="required">*</span>
            </label>
            <input
              type="number"
              id="payIn"
              name="payIn"
              value={formData.payIn}
              onChange={handleInputChange}
              placeholder="e.g. 1.71"
              required
              disabled={loading}
              step="0.01"
              min="0.01"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="payOut">
              PayOut rate <span className="required">*</span>
            </label>
            <input
              type="number"
              id="payOut"
              name="payOut"
              value={formData.payOut}
              onChange={handleInputChange}
              placeholder="e.g. 1.72"
              required
              disabled={loading}
              step="0.01"
              min="0.01"
              className="form-input"
            />
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save' : 'Create')}
            </button>
          </div>
          </form>
        </div>
      </div>
    </DetailsPortal>
  );
};

export default AddPriceModal;
